import { useContext, useMemo, useState } from 'react';
import { View, Text, ScrollView, Platform, ActivityIndicator } from 'react-native';
import { useRouter, useSegments } from 'expo-router';
import { useUnistyles, StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { guides, getGuide, GUIDES_ENABLED } from '@/config/guides';
import { eligibleSteps, guideStatus } from '@/utils/guides';
import { GuideContext, GuideProgressContext } from '@/components/Guides/GuideProvider';
import GuidePreferences from '@/components/Guides/GuidePreferences';
import SearchBox from '@/components/General/inputs/SearchBox';
import SolidButton from '@/components/General/SolidButton';

export default function GuidesScreen() {
    const guide = useContext(GuideContext);
    const progress = useContext(GuideProgressContext);
    const router = useRouter();
    const segments = useSegments();
    const { rt: { isLandscape } } = useUnistyles();
    const [search, setSearch] = useState('');
    const { data: user, isLoading: userLoading } = Query.people.loggedInUser();
    const formsEnabled = !!getGuide('forms.dashboard');
    const { data: forms = [], isLoading: formsLoading } = Query.forms.all({ options: { enabled: formsEnabled } });
    const { data: myEntries = [], isLoading: entriesLoading } = Query.formEntries.mine({ options: { enabled: formsEnabled && !!user?.id } });
    const context = useMemo(() => ({ user, organizationID: user?.organizationID, isLandscape, isWeb: Platform.OS === 'web', forms, myEntries }), [user, isLandscape, forms, myEntries]);
    const available = useMemo(() => GUIDES_ENABLED ? guides.map(item => ({ ...item, steps: eligibleSteps(item, context) })).filter(item => item.steps.length && `${item.module} ${item.title} ${item.summary}`.toLowerCase().includes(search.toLowerCase())) : [], [context, search]);
    const openKnowledgeBase = () => {
        if (Platform.OS === 'web') window.open('https://help.reachchms.com', '_blank', 'noopener,noreferrer');
        else {
            const tab = segments[0] === '(tabs)' ? segments[1] : '(home)';
            router.push({ pathname: `/(tabs)/${tab}/(utilities)/webview`, params: { url: 'https://help.reachchms.com', title: 'Knowledge Base' } });
        }
    };
    return <ScrollView testID="guides-screen" contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.hero}>
            <Text style={styles.title}>Guides & Help</Text>
            <Text style={styles.body}>Short walkthroughs for features available to you. Viewed and skipped steps stay remembered across your organizations and devices.</Text>
            <SolidButton text="Open Knowledge Base" size="sm" variant="muted" iconName="open-outline" iconBefore onPress={openKnowledgeBase} testID="guides-knowledge-base" />
        </View>
        <View style={styles.card}><GuidePreferences testID="guides-hub-auto-setting" /></View>
        <SearchBox testID="Guides" searchText={search} onChangeText={setSearch} placeholder="Search guides" textChangeDelay={100} />
        {progress?.isError && <Text style={styles.body}>Saved progress is temporarily unavailable. You can still replay available guides.</Text>}
        {userLoading || formsLoading || entriesLoading ? <ActivityIndicator /> : available.length ? available.map(item => {
            const status = guideStatus(item.steps, progress?.state?.progress);
            return <View style={styles.card} testID={`guide-item-${item.id}`} key={item.id}>
                <Text style={styles.module}>{item.module}</Text>
                <Text style={styles.heading}>{item.title}</Text>
                <Text style={styles.body}>{item.summary}</Text>
                <View style={styles.row}>
                    <Text testID={`guide-status-${item.id}`} style={styles.body}>{progress?.state ? `${status.label} · ${status.viewed}/${status.total} viewed` : 'Progress unavailable'}</Text>
                    <View style={styles.row}>
                        {status.canContinue && <SolidButton text="Continue" size="sm" onPress={() => guide?.replay(item.id, { resume: true })} testID={`guide-continue-${item.id}`} />}
                        <SolidButton text={status.label === 'New' ? 'Start guide' : 'Replay'} size="sm" variant={status.canContinue ? 'muted' : undefined} onPress={() => guide?.replay(item.id)} testID={`guide-start-${item.id}`} />
                    </View>
                </View>
            </View>;
        }) : <Text style={styles.body}>{GUIDES_ENABLED ? 'No matching guides are available in your current context.' : 'Guides are not currently available. Visit the Knowledge Base for help.'}</Text>}
    </ScrollView>;
}

const styles = StyleSheet.create(({ colors, fonts, typography, spacing, roundness, shadows }) => ({
    content: { padding: spacing.lg, gap: spacing.md, width: '100%', maxWidth: 900, alignSelf: 'center', paddingBottom: spacing.xxl },
    hero: { gap: spacing.sm },
    title: { fontFamily: fonts.bold, fontSize: typography.hero, color: colors.text },
    heading: { fontFamily: fonts.bold, fontSize: typography.h2, color: colors.text },
    body: { fontFamily: fonts.body, fontSize: typography.body, color: colors.textSecondary, lineHeight: 23 },
    module: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.primary },
    card: { padding: spacing.md, gap: spacing.sm, borderRadius: roundness.lg, backgroundColor: colors.surface, boxShadow: shadows.card },
    row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, flexWrap: 'wrap' },
}));
